import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Loader2, Users, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const ACCEPTED_COLUMNS = ["name", "email", "phone", "address", "branches", "services_history", "notes"];

function parseCSV(text) {
  // Minimal CSV parser that handles quoted fields & commas inside quotes.
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        field += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === "," || c === ";" || c === "\t") {
        cur.push(field);
        field = "";
      } else if (c === "\n") {
        cur.push(field);
        rows.push(cur);
        cur = [];
        field = "";
      } else if (c === "\r") {
        // ignore
      } else {
        field += c;
      }
    }
  }
  if (field.length || cur.length) {
    cur.push(field);
    rows.push(cur);
  }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

function normalizeHeader(h) {
  const k = h.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (["name", "nom", "prenom", "client", "fullname"].includes(k)) return "name";
  if (["email", "mail", "courriel", "e-mail"].includes(k)) return "email";
  if (["phone", "tel", "telephone", "mobile", "portable"].includes(k)) return "phone";
  if (["address", "adresse"].includes(k)) return "address";
  if (["branches", "branche", "poles", "pole"].includes(k)) return "branches";
  if (["services_history", "services", "prestations", "historique"].includes(k)) return "services_history";
  if (["notes", "note", "commentaire"].includes(k)) return "notes";
  return null;
}

export default function AdminCustomers() {
  const { user, ready } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, mode }
  const fileRef = useRef(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/admin/customers");
      setList(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) fetchAll();
  }, [ready, user]);

  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const rows = parseCSV(text);
    if (rows.length < 2) {
      toast.error("Le fichier semble vide ou mal formé.");
      return;
    }
    const headers = rows[0].map(normalizeHeader);
    if (!headers.includes("name")) {
      toast.error("Une colonne 'name' (ou 'nom') est obligatoire dans la 1ère ligne.");
      return;
    }
    const customers = rows.slice(1).map((r) => {
      const obj = {};
      headers.forEach((h, i) => {
        if (h && ACCEPTED_COLUMNS.includes(h)) obj[h] = (r[i] || "").trim();
      });
      return obj;
    }).filter((c) => c.name);
    setPreview({ rows: customers, mode: "append" });
  };

  const doImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/customers/import", preview);
      toast.success(`${data.inserted} client(s) importé(s).`);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    } finally {
      setImporting(false);
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    try {
      await api.delete(`/admin/customers/${id}`);
      setList((l) => l.filter((c) => c.id !== id));
      toast.success("Supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Supprimer TOUS les clients ? Cette action est irréversible.")) return;
    try {
      const { data } = await api.delete("/admin/customers");
      toast.success(`${data.deleted} client(s) supprimé(s).`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-admin-customers">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6"
          data-testid="customers-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Base client</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              <Users className="inline -mt-1 mr-2" size={32} /> Clients
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              Importez votre base client (CSV) pour que Gabriel puisse les identifier automatiquement.
            </p>
          </div>
        </div>

        {/* Import zone */}
        <div className="mt-8 rounded-xl border border-white/10 bg-[#0e0e0e] p-6" data-testid="customers-import">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <FileSpreadsheet size={16} /> Importer un fichier CSV
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Colonnes acceptées : <code className="text-zinc-300">name, email, phone, address, branches, services_history, notes</code>.
                Délimiteurs supportés : virgule, point-virgule, tabulation. La colonne <strong>name</strong> est obligatoire.
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <a
                href="/clients_template.csv"
                download
                data-testid="customers-template-download"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 hover:bg-white/5 text-sm"
              >
                <Download size={14} /> Modèle CSV
              </a>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={onFile}
                data-testid="customers-file-input"
                className="hidden"
              />
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="customers-pick-file"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 hover:bg-white/5 text-sm"
              >
                <Upload size={14} /> Choisir un fichier
              </button>
              {list.length > 0 && (
                <button
                  onClick={clearAll}
                  data-testid="customers-clear-all"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-sm"
                >
                  <Trash2 size={14} /> Tout vider
                </button>
              )}
            </div>
          </div>

          {preview && (
            <div className="mt-6 border-t border-white/10 pt-6" data-testid="customers-preview">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm">
                  <strong>{preview.rows.length}</strong> client(s) prêt(s) à être importé(s).
                </div>
                <div className="flex gap-2">
                  <label className="text-xs text-zinc-400 flex items-center gap-2">
                    <input
                      type="radio"
                      name="mode"
                      checked={preview.mode === "append"}
                      onChange={() => setPreview({ ...preview, mode: "append" })}
                    />{" "}
                    Ajouter
                  </label>
                  <label className="text-xs text-zinc-400 flex items-center gap-2">
                    <input
                      type="radio"
                      name="mode"
                      checked={preview.mode === "replace"}
                      onChange={() => setPreview({ ...preview, mode: "replace" })}
                    />{" "}
                    Remplacer (efface l'existant)
                  </label>
                </div>
              </div>
              <div className="max-h-60 overflow-auto rounded border border-white/10 bg-black text-xs">
                <table className="w-full">
                  <thead className="text-zinc-500 uppercase tracking-wider">
                    <tr>
                      {ACCEPTED_COLUMNS.map((c) => (
                        <th key={c} className="text-left px-3 py-2 border-b border-white/5">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-b border-white/5">
                        {ACCEPTED_COLUMNS.map((c) => (
                          <td key={c} className="px-3 py-2 text-zinc-300">{r[c] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={doImport}
                  disabled={importing}
                  data-testid="customers-confirm-import"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-white text-black font-semibold disabled:opacity-50"
                >
                  {importing && <Loader2 size={14} className="animate-spin" />}
                  Confirmer l'import
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="px-5 py-2 rounded-md border border-white/15 text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>

        {/* List */}
        <div className="mt-10 rounded-xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-zinc-400">
              <Loader2 className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : list.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="customers-empty">
              Aucun client. Importez votre base via le bouton ci-dessus.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customers-table">
                <thead className="bg-[#111] text-zinc-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Nom</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Téléphone</th>
                    <th className="text-left px-5 py-3">Pôles</th>
                    <th className="text-left px-5 py-3">Prestations</th>
                    <th className="text-right px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`customer-row-${c.id}`}>
                      <td className="px-5 py-3 font-medium">{c.name}</td>
                      <td className="px-5 py-3 text-zinc-400">{c.email || "—"}</td>
                      <td className="px-5 py-3 text-zinc-400">{c.phone || "—"}</td>
                      <td className="px-5 py-3 text-zinc-400">{c.branches || "—"}</td>
                      <td className="px-5 py-3 text-zinc-400 max-w-sm truncate">{c.services_history || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => removeOne(c.id)}
                          data-testid={`customer-delete-${c.id}`}
                          className="p-2 rounded hover:bg-white/10 text-zinc-400 hover:text-rose-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
